import { useEffect, useState } from 'react';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';
import { TokenExchangeStatus } from 'V2/Checkout/CheckoutVerification/types';
import { CHECKOUT_SESSION_KEYS } from 'V2/Checkout/constants';

interface TokenExchangeResponse {
  status: 'ok' | 'verified' | 'bound';
  insuranceApplicationId?: string;
  quoteId?: string;
  paymentPlanId?: string;
}

export const useTokenExchange = (
  token: string
): { status: TokenExchangeStatus; errorMessage?: string } => {
  const [status, setStatus] = useState<TokenExchangeStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    const controller = new AbortController();

    httpCheckoutAPI
      .get({ url: `/checkout/${token}`, signal: controller.signal })
      .then((response) => {
        const data = response.data as TokenExchangeResponse;

        if (data.status === 'bound') {
          // Already bound — store appId so confirmation page can load bind_summary
          if (data.insuranceApplicationId) {
            sessionStorage.setItem(
              CHECKOUT_SESSION_KEYS.appId,
              data.insuranceApplicationId
            );
          }
          setStatus('bound');
          return;
        }

        if (data.status === 'verified') {
          // ZIP already verified — skip ZIP form, go straight to payment
          if (data.insuranceApplicationId) {
            sessionStorage.setItem(
              CHECKOUT_SESSION_KEYS.appId,
              data.insuranceApplicationId
            );
          }
          if (data.quoteId) {
            sessionStorage.setItem(CHECKOUT_SESSION_KEYS.quoteId, data.quoteId);
          }
          if (data.paymentPlanId) {
            sessionStorage.setItem(
              CHECKOUT_SESSION_KEYS.paymentPlanId,
              data.paymentPlanId
            );
          }
          setStatus('verified');
          return;
        }

        setStatus('ready');
      })
      .catch((err: any) => {
        if (err.code === 'ERR_CANCELED') return;
        const httpStatus = err.response?.status;
        const errorType = err.response?.data?.error_type;
        switch (true) {
          case errorType === 'expired':
          case httpStatus === 410:
            setStatus('expired');
            break;
          case errorType === 'invalidated':
          case httpStatus === 409:
            setStatus('invalidated');
            break;
          case errorType === 'locked':
            setErrorMessage(err.response?.data?.message);
            setStatus('locked');
            break;
          case httpStatus === 401:
          case httpStatus === 403:
            // 401: no session cookie at all (e.g. incognito).
            // 403: token reused without a session cookie
            // Both mean the user has no active session — show the ZIP form to re-authenticate.
            setStatus('ready');
            break;
          default:
            setStatus('error');
        }
      });

    return () => controller.abort();
  }, [token]);

  return { status, errorMessage };
};
